import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Notification from "../components/Notification";
import { API_URL } from "../config";
import '../styles/SignIn.css';
import { useRecaptcha, ROBOT_MESSAGE } from "../components/Recaptcha";

function LogIn() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "", isVisible: false });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const robot = useRecaptcha();

    // Validation states
    const [errors, setErrors] = useState({
        email: "",
        password: ""
    });

    const validate = () => {
        let isValid = true;
        const errors = {};

        if (!email) {
            errors.email = "Email is required.";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            errors.email = "Email is invalid.";
            isValid = false;
        }

        if (!password) {
            errors.password = "Password is required.";
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    useEffect(() => {
        document.title = "Sign In - CFCM Portal";
        const token = localStorage.getItem("access_token");
        const tempToken = localStorage.getItem("temp_token");
        if (tempToken) {
            localStorage.removeItem("temp_token");
        }
        if (token) {
            navigate("/dashboard");
        }
    }, [navigate]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        if (validate()) {
            if (robot.missing) {
                setNotification({ type: "danger", message: ROBOT_MESSAGE, isVisible: true });
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(API_URL + "/auth/login", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password,
                        recaptcha_token: robot.token
                    }),
                });

                const data = await response.json();
                robot.reset(); // An answer can be used only once.

                if (response.ok) {
                    // Save the token to localStorage
                    if (data.token) {
                        localStorage.setItem("access_token", data.token);
                        // After successful login, store user data
                        localStorage.setItem('user', JSON.stringify(data.user));
                        localStorage.setItem('userRole', data.role);

                        setNotification({ type: "success", message: "Login successful! Redirecting ...", isVisible: true });
                        setTimeout(() => {
                            // Redirect to the previous page or dashboard
                            console.log("Location state:", location.state);
                            // Handle redirect - improved logic
                            const from = location.state?.from || { pathname: '/dashboard' };
                            navigate(from.pathname, {
                                replace: true,
                                state: { from: undefined }  // Clear the state after use
                            });

                        }, 2000);
                    } else if (data.status === "Expired") {
                        localStorage.setItem("temp_token", data.temp_token);
                       // console.log("EXPIRED TOKEN", data.temp_token);
                        setNotification({ type: "danger", message: data.error || "Login failed.", isVisible: true });
                        setTimeout(() => {
                            navigate("/account/activation");
                        }, 6000);

                    } else if (data.temp_token) {
                        localStorage.setItem("temp_token", data.temp_token);
                        setNotification({ type: "danger", message: data.error || "Login failed.", isVisible: true });
                        setTimeout(() => {
                            navigate("/signup/more-details");
                        }, 13000);

                    } else if (data.error) {
                        setNotification({ type: "danger", message: data.error || "Login failed.", isVisible: true });
                    } else {
                        setNotification({ type: "danger", message: "Login failed.", isVisible: true });
                    }
                    setEmail("")
                    setPassword("")
                    setLoading(false);
                } else {
                    setNotification({ type: "danger", message: data.error || "Login failed.", isVisible: true });
                    if (data.errors) {
                        setErrors({
                            ...errors,
                            ...data.errors
                        });
                    }
                    setLoading(false)
                }

            } catch (error) {
                console.log(error)
                setNotification({ type: "danger", message: "An error occurred. Please try again later.", isVisible: true });
                setLoading(false)
            }

        } else {
            setNotification({ type: "danger", message: "Please fix the errors in the form.", isVisible: true });
            setLoading(false)
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, isVisible: false });
    };

    return (
        <div className="cf-public">
            <Header />
            <main>
                <div className="signin-page">
                    <div className="signin-card">
                        <div className="signin-content">
                            <div className="content">
                                <h1 className="sign-in-title">Sign in</h1>

                                <p className="cf-muted mb-4">Sign in to your CFCM Portal account.</p>

                                {notification.isVisible && (
                                    <Notification
                                        type={notification.type}
                                        message={notification.message}
                                        onClose={handleCloseNotification}
                                    />
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className={`field ${errors.email ? 'is-danger' : ''}`}>
                                        <label className="label">Email</label>
                                        <div className="control has-icons-left has-icons-right">
                                            <input
                                                className={`input ${errors.email ? 'is-danger' : ''}`}
                                                type="email"
                                                placeholder="you@example.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                            <span className="icon is-small is-left">
                                                <i className="fas fa-envelope"></i>
                                            </span>
                                            {errors.email && (
                                                <span className="icon is-small is-right">
                                                    <i className="fas fa-exclamation-triangle"></i>
                                                </span>
                                            )}
                                        </div>
                                        {errors.email && <p className="help is-danger">{errors.email}</p>}
                                    </div>

                                    <div className={`field ${errors.password ? 'is-danger' : ''}`}>
                                        <label className="label">Password</label>
                                        <div className="control has-icons-left has-icons-right">
                                            <input
                                                className={`input ${errors.password ? 'is-danger' : ''}`}
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="Enter password"
                                            />
                                            <span className="icon is-small is-left">
                                                <i className="fas fa-lock"></i>
                                            </span>
                                        </div>
                                        {errors.password && <p className="help is-danger">{errors.password}</p>}
                                    </div>

                                    {robot.element}

                                    <div className="field">
                                        <div className="control">
                                            <button type="submit" className="button is-fullwidth primary-bg" disabled={loading}>
                                                {loading ? "Loading..." : "Sign In"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                <p className="cf-auth-links">
                                    <Link to="/account/forgot-password">Forgot your password?</Link>
                                </p>
                                <p className="cf-auth-links" style={{ marginTop: 6 }}>
                                    New to the portal? <Link to="/signup">Become a member</Link>
                                </p>

                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default LogIn;
